import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  ArrowUpDown,
  Flag,
  User,
  Paperclip,
  Lock,
  Plus,
  AlertTriangle,
  Loader2,
  Check,
  X,
  Monitor,
  Tag,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { fetchTicket, updateTicketApi, fetchInternalUsers, fetchAllUsers, fetchMentionableUsers, postComment, addWatcherApi, removeWatcherApi } from '@/hooks/useTickets';
import { MentionInput } from '@/components/tickets/MentionInput';
import { buildStorageComment } from '@/lib/mentions';
import type { TicketDetail as TicketDetailType, InternalUserItem, BuildingGroupEntry, DmpModificationEntry } from '@uniticket/shared';
import { VALID_TRANSITIONS, TRANSITION_ROLES, PRIORITIES, PRIORITY_LEVEL } from '@uniticket/shared';
import { StatusBadge } from '@/components/custom/StatusBadge';
import { PriorityBadge } from '@/components/custom/PriorityBadge';
import { TypeBadge } from '@/components/custom/TypeBadge';
import { TicketTimeline } from '@/components/tickets/TicketTimeline';
import { TicketDMPPanel } from '@/components/tickets/TicketDMPPanel';
import { OrderActionPanel } from '@/components/tickets/OrderActionViews';
import { TicketAttachments } from '@/components/tickets/TicketAttachments';
import type { TicketAttachmentsHandle } from '@/components/tickets/TicketAttachments';
import { EditTicketDialog } from '@/components/tickets/EditTicketDialog';
import { EscalateTicketDialog } from '@/components/tickets/EscalateTicketDialog';
import { BuildingGroupTable } from '@/components/custom/BuildingGroupTable';
import { DmpFieldWithDiff } from '@/components/custom/DmpFieldWithDiff';
import { SimilarTicketsPanel } from '@/components/tickets/SimilarTicketsPanel';
import { BuildAreaMapPanel } from '@/components/tickets/BuildAreaMapPanel';
import { AttachedJvidsPanel } from '@/components/tickets/AttachedJvidsPanel';
import { AddressEnrichmentPanel } from '@/components/tickets/AddressEnrichmentPanel';

function getInitials(name: string) {
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) +
    ', ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export function TicketDetail() {
  const { t } = useTranslation(['tickets', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: authUser, isInternal, isExternal, isAdmin } = useAuth();
  const [internalNote, setInternalNote] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [ticket, setTicket] = useState<TicketDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [openAction, setOpenAction] = useState<'status' | 'priority' | 'reassign' | null>(null);
  const [internalUsers, setInternalUsers] = useState<InternalUserItem[]>([]);
  const [updating, setUpdating] = useState(false);
  const [posting, setPosting] = useState(false);
  const [showWatcherAdd, setShowWatcherAdd] = useState(false);
  const [allUsers, setAllUsers] = useState<InternalUserItem[]>([]);
  const [watcherSearch, setWatcherSearch] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);
  const attachmentsComponentRef = useRef<TicketAttachmentsHandle>(null);

  const filteredWatcherUsers = useMemo(() => {
    if (!ticket) return { internal: [], external: [] };
    const available = allUsers.filter((u) => !ticket.watchers.some((w) => w.userId === u.id));
    const q = watcherSearch.toLowerCase().trim();
    const filtered = q
      ? available.filter((u) => u.displayName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
      : available;
    return {
      internal: filtered.filter((u) => u.role !== 'EXTERNAL_OPERATOR_USER'),
      external: filtered.filter((u) => u.role === 'EXTERNAL_OPERATOR_USER'),
    };
  }, [allUsers, ticket, watcherSearch]);

  // Build a user ID -> display name map for mention rendering in timeline
  const userMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of allUsers) {
      map.set(u.id, u.displayName);
    }
    return map;
  }, [allUsers]);

  const loadTicket = useCallback(() => {
    if (!id) return;
    setLoading(true);
    fetchTicket(id)
      .then((res) => setTicket(res.data))
      .catch(() => toast.error(t('tickets:detail.loadError')))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { loadTicket(); }, [loadTicket]);

  const loadAllUsers = useCallback(() => {
    const fetcher = isExternal ? fetchMentionableUsers : fetchAllUsers;
    return fetcher().then((res) => setAllUsers(res.data as InternalUserItem[]));
  }, [isExternal]);

  // Eagerly load all users for mention rendering in timeline
  useEffect(() => {
    if (allUsers.length === 0) {
      loadAllUsers().catch(() => { /* silent — mention names will show as 'Unknown User' */ });
    }
  }, []);

  // Lazy-load internal users when Reassign dropdown opens
  useEffect(() => {
    if (openAction === 'reassign' && internalUsers.length === 0) {
      fetchInternalUsers()
        .then((res) => setInternalUsers(res.data))
        .catch(() => toast.error(t('tickets:detail.loadUsersError')));
    }
  }, [openAction, internalUsers.length]);

  async function handlePostComment() {
    if (!ticket || !commentText.trim()) return;
    setPosting(true);
    try {
      const res = await postComment(ticket.id, {
        comment: buildStorageComment(commentText.trim(), mentionedUserIds, userMap),
        isInternal: internalNote,
        mentionedUserIds: mentionedUserIds.length > 0 ? mentionedUserIds : undefined,
      });
      setTicket(res.data);
      setCommentText('');
      setInternalNote(false);
      setMentionedUserIds([]);
      toast.success(t('tickets:detail.commentPosted'));
    } catch (err: any) {
      toast.error(err?.message || t('tickets:detail.commentError'));
    } finally {
      setPosting(false);
    }
  }

  async function handlePostAndResolve() {
    if (!ticket || !commentText.trim()) return;
    setPosting(true);
    try {
      await postComment(ticket.id, {
        comment: buildStorageComment(commentText.trim(), mentionedUserIds, userMap),
        isInternal: internalNote,
        resolveAfter: true,
        mentionedUserIds: mentionedUserIds.length > 0 ? mentionedUserIds : undefined,
      });
      toast.success(t('tickets:detail.resolvedAndRedirecting'));
      navigate('/tickets');
    } catch (err: any) {
      toast.error(err?.message || t('tickets:detail.updateError'));
    } finally {
      setPosting(false);
    }
  }

  async function handleAddWatcher(userId: string) {
    if (!ticket) return;
    try {
      const res = await addWatcherApi(ticket.id, userId);
      setTicket(res.data);
      setShowWatcherAdd(false);
      toast.success(t('tickets:detail.watcherAdded'));
    } catch (err: any) {
      toast.error(err?.message || t('tickets:detail.watcherAddError'));
    }
  }

  async function handleRemoveWatcher(userId: string) {
    if (!ticket) return;
    try {
      const res = await removeWatcherApi(ticket.id, userId);
      setTicket(res.data);
      toast.success(t('tickets:detail.watcherRemoved'));
    } catch (err: any) {
      toast.error(err?.message || t('tickets:detail.watcherRemoveError'));
    }
  }

  async function handleUpdate(data: Record<string, unknown>) {
    if (!ticket) return;
    setUpdating(true);
    try {
      await updateTicketApi(ticket.id, data as any);
      toast.success(t('tickets:detail.ticketUpdated'));
      setOpenAction(null);
      loadTicket();
    } catch (err: any) {
      toast.error(err?.message || t('tickets:detail.updateError'));
    } finally {
      setUpdating(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-brand-rose/30 border-t-brand-rose rounded-full animate-spin" />
          <p className="text-sm text-slate-500">{t('tickets:detail.loadingTicket')}</p>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <p className="text-sm text-slate-600">{t('tickets:detail.ticketNotFound')}</p>
        <Link to="/tickets" className="text-sm text-brand-rose hover:text-brand-purple transition-colors">
          {t('tickets:detail.backToTickets')}
        </Link>
      </div>
    );
  }

  // PRD 4.1.4: Conditional action visibility based on ticket status + role
  const canEdit = ticket.status !== 'CLOSED' && (isInternal || ticket.createdBy.id === authUser?.id);
  // Escalation: same-operator users or admin, only on OPEN/IN_PROGRESS
  const isSameOperator = ticket.operator.id === authUser?.operatorId;
  const canEscalateOrDeescalate = (isAdmin || isSameOperator) &&
    (ticket.status === 'OPEN' || ticket.status === 'IN_PROGRESS');
  const escalateMode: 'escalate' | 'deescalate' = ticket.escalated ? 'deescalate' : 'escalate';
  // Cooldown: external users must wait until escalationEligibleAt. Admins bypass.
  const cooldownActive = !isAdmin &&
    escalateMode === 'escalate' &&
    ticket.escalationEligibleAt != null &&
    new Date(ticket.escalationEligibleAt).getTime() > Date.now();
  const cooldownTooltip = cooldownActive && ticket.escalationEligibleAt
    ? t('tickets:detail.escalationCooldownTooltip', { eligibleAt: formatDate(ticket.escalationEligibleAt) })
    : undefined;
  const canReassign = isInternal && ticket.status !== 'CLOSED' && ticket.status !== 'RESOLVED';
  const canChangePriority = ticket.status !== 'CLOSED' && ticket.status !== 'RESOLVED';

  // Filter status transitions to only those the user's role can perform
  const allowedTransitions = (VALID_TRANSITIONS[ticket.status] ?? []).filter((target) => {
    const key = `${ticket.status}->${target}`;
    const roles = TRANSITION_ROLES[key];
    return !roles || roles.includes(authUser?.role ?? '');
  });

  return (
    <div>
      {/* Back link */}
      <Link to="/tickets" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand-rose transition-colors mb-4">
        <ArrowLeft className="w-4 h-4" />
        {t('tickets:detail.backToTickets')}
      </Link>

      {/* Three-column layout on desktop */}
      <div className="flex gap-6 flex-col lg:flex-row">
        {/* LEFT + CENTER: Main content */}
        <div className="flex-1 min-w-0 space-y-4">
          {/* Ticket header */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm font-bold text-brand-rose">{ticket.ticketNumber}</span>
                  <TypeBadge type={ticket.ticketType} />
                  {ticket.orderSubcategory && (
                    <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-purple-50 text-purple-700 border-purple-200">
                      {t(`common:orderSubcategory.${ticket.orderSubcategory}`, ticket.orderSubcategory)}
                    </span>
                  )}
                  <StatusBadge status={ticket.status} />
                  {ticket.escalated && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-red-50 text-red-700 border-red-200" title={ticket.escalationReason ?? undefined}>
                      <AlertTriangle className="w-3 h-3" />
                      {t('tickets:detail.escalated')}
                    </span>
                  )}
                  <PriorityBadge priority={ticket.priority} />
                </div>
                <h2 className="text-xl font-bold text-slate-900 leading-tight">{ticket.subject}</h2>
                <p className="text-sm text-slate-500">
                  {t('tickets:detail.openedBy')} <span className="font-medium text-slate-700">{ticket.createdBy.displayName}</span>
                  {ticket.operator.code !== 'UNF' && ` (${ticket.operator.name})`}
                  {' '}&middot; {formatDate(ticket.createdAt)}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {canEdit && (
                  <button onClick={() => setEditOpen(true)} className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-1.5">
                    <Edit className="w-3.5 h-3.5" />
                    {t('common:actions.edit')}
                  </button>
                )}
                {canEscalateOrDeescalate && (
                  <button
                    onClick={() => setEscalateOpen(true)}
                    disabled={cooldownActive}
                    title={cooldownTooltip}
                    className={`px-3 py-1.5 text-xs font-semibold text-white rounded-lg flex items-center gap-1.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                      escalateMode === 'escalate'
                        ? 'bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose'
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    <ArrowUpDown className="w-3.5 h-3.5" />
                    {escalateMode === 'escalate' ? t('tickets:detail.escalate') : t('tickets:detail.deescalate')}
                  </button>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{ticket.description ?? ''}</p>
              {ticket.dmpOrderId && (
                <p className="text-sm text-slate-700 leading-relaxed mt-2">
                  {t('tickets:detail.dmpOrderRef')} <span className="font-mono font-medium text-brand-rose">{ticket.dmpOrderId}</span>
                </p>
              )}
            </div>

            {/* Escalation reason */}
            {ticket.escalated && ticket.escalationReason && (
              <div className="mt-3 p-3 bg-red-50 border border-red-100 rounded-lg">
                <p className="text-[10px] font-bold uppercase tracking-wider text-red-400 mb-1">{t('tickets:detail.escalationReason')}</p>
                <p className="text-sm text-red-700">{ticket.escalationReason}</p>
              </div>
            )}

            {/* Metadata grid */}
            <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">{t('common:fields.operator')}</p>
                <p className="text-sm font-medium text-slate-800">{ticket.operator.name}</p>
              </div>
              {/* PRD 9.2.4: Hide "Assigned To" from external users */}
              {isInternal && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">{t('common:fields.assignedTo')}</p>
                  {ticket.assignee ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-[8px] font-bold text-slate-600">
                        {getInitials(ticket.assignee.displayName)}
                      </div>
                      <p className="text-sm font-medium text-slate-800">{ticket.assignee.displayName}</p>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400">{t('common:fields.unassigned')}</p>
                  )}
                </div>
              )}
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">{t('common:fields.created')}</p>
                <p className="text-sm text-slate-600">{formatDate(ticket.createdAt)}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">{t('common:fields.lastUpdated')}</p>
                <p className="text-sm text-slate-600">{formatDate(ticket.updatedAt)}</p>
              </div>
            </div>
          </div>

          {/* DMP Order Data (for open orders) */}
          {ticket.ticketType === 'OPEN_ORDER' && (ticket.dmpOrderId || ticket.orderSubcategory === 'DISTANCE_INQUIRY' || ticket.utac || ticket.utacSwap1 || ticket.utacSwap2 || ticket.splitterSwapFrom || ticket.splitterSwapTo || ticket.customerReference || ticket.moveReason || ticket.operatorOrderRef || ticket.customerNumber) && <TicketDMPPanel ticket={ticket} />}

          {/* Order automation (patch/depatch via the Unifiber Ordering API). Shows for any ticket
              that already has an order result, OR — for internal users — on eligible MANUAL_PATCH/
              MANUAL_DEPATCH tickets with no order yet, so the assignee can run it from here. */}
          {(ticket.orderAction ||
            (isInternal && ticket.ticketType === 'OPEN_ORDER' &&
              (ticket.orderSubcategory === 'MANUAL_PATCH' || ticket.orderSubcategory === 'MANUAL_DEPATCH'))) && (
            <OrderActionPanel
              orderAction={ticket.orderAction ?? null}
              isInternal={isInternal}
              ticketId={ticket.id}
              ticketOperatorCode={ticket.operator.code}
              ticketSubcategory={ticket.orderSubcategory ?? null}
              onRetried={loadTicket}
            />
          )}

          {/* Address Classification (for address management tickets) */}
          {ticket.ticketType === 'ADDRESS_MANAGEMENT' && (ticket.addrSource || ticket.addrAction || ticket.addrStatusField || ticket.addrScope) && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-sm font-bold text-slate-800 mb-3">{t('tickets:detail.addressClassification')}</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{t('tickets:detail.fieldAddrSource')}</p>
                  <p className="text-sm text-slate-700">{ticket.addrSource ? t(`common:addressSource.${ticket.addrSource}`, ticket.addrSource) : '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{t('tickets:detail.fieldAddrAction')}</p>
                  <p className="text-sm text-slate-700">{ticket.addrAction ? t(`common:addressAction.${ticket.addrAction}`, ticket.addrAction) : '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{t('tickets:detail.fieldAddrStatus')}</p>
                  <p className="text-sm text-slate-700">{ticket.addrStatusField ? t(`common:addressStatus.${ticket.addrStatusField}`, ticket.addrStatusField) : '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{t('tickets:detail.fieldAddrScope')}</p>
                  <p className="text-sm text-slate-700">{ticket.addrScope ? t(`common:addressScope.${ticket.addrScope}`, ticket.addrScope) : '—'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Address data (for address and open order tickets) */}
          {(ticket.ticketType === 'ADDRESS_MANAGEMENT' || ticket.ticketType === 'OPEN_ORDER') && (ticket.addrDmpFetched || ticket.addrStreet || ticket.jvid) && (() => {
            const mods = ticket.dmpModifications as Record<string, DmpModificationEntry> | null;
            const isAddress = ticket.ticketType === 'ADDRESS_MANAGEMENT';
            return (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-sm font-bold text-slate-800">{t('tickets:detail.addressData')}</h3>
                {mods && (
                  <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                    {t('tickets:detail.fieldsModified', { count: Object.keys(mods).length })}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
                <DmpFieldWithDiff label={t('common:fields.jvid')} value={ticket.jvid != null ? String(ticket.jvid) : '—'} mono modification={mods?.['JVID']} />
                <DmpFieldWithDiff label={t('tickets:detail.fieldStreet')} value={ticket.addrStreet ?? '—'} modification={mods?.['Street']} />
                <DmpFieldWithDiff label={t('tickets:detail.fieldHouseNumber')} value={ticket.addrHouseNumber ?? '—'} modification={mods?.['House Number']} />
                <DmpFieldWithDiff label={t('tickets:detail.fieldHouseNumberExt')} value={ticket.addrHouseNumberExt ?? '—'} modification={mods?.['House Number Extension']} />
                <DmpFieldWithDiff label={t('tickets:detail.fieldBusNumber')} value={ticket.addrBusNumber ?? '—'} modification={mods?.['Bus Number']} />
                <DmpFieldWithDiff label={t('tickets:detail.fieldPostalCode')} value={ticket.addrPostalCode ?? '—'} modification={mods?.['Postal Code']} />
                <DmpFieldWithDiff label={t('tickets:detail.fieldMunicipality')} value={ticket.addrMunicipality ?? '—'} modification={mods?.['Municipality']} />
                <DmpFieldWithDiff label={t('tickets:detail.fieldCity')} value={ticket.addrCity ?? '—'} modification={mods?.['City']} />
                <DmpFieldWithDiff label={t('tickets:detail.fieldLatitude')} value={ticket.addrLat != null ? String(ticket.addrLat) : '—'} modification={mods?.['Latitude']} />
                <DmpFieldWithDiff label={t('tickets:detail.fieldLongitude')} value={ticket.addrLon != null ? String(ticket.addrLon) : '—'} modification={mods?.['Longitude']} />
                <DmpFieldWithDiff label={t('tickets:detail.fieldDwellingUnitType')} value={ticket.addrDwellingUnitType ?? '—'} modification={mods?.['Dwelling Unit Type']} />
                {isAddress && (
                  <>
                    <DmpFieldWithDiff label={t('tickets:detail.fieldVolumeMdu')} value={ticket.addrVolumeMdu != null ? String(ticket.addrVolumeMdu) : '—'} modification={mods?.['Volume MDU']} />
                    <DmpFieldWithDiff label={t('tickets:detail.fieldExistingBoxes')} value={ticket.addrExistingBoxes ?? '—'} modification={mods?.['Existing Boxes']} />
                  </>
                )}
                <DmpFieldWithDiff label={t('tickets:detail.fieldBelmapAddressId')} value={ticket.belmapAddressId != null ? String(ticket.belmapAddressId) : '—'} mono modification={mods?.['Belmap Address ID']} />
                <DmpFieldWithDiff label={t('tickets:detail.fieldBelmapBuildingId')} value={ticket.belmapBuildingId != null ? String(ticket.belmapBuildingId) : '—'} mono modification={mods?.['Belmap Building ID']} />
              </div>

              {/* Building Group sub-table (read-only) — Address Management only */}
              {isAddress && ticket.addrBuildingGroupData && (ticket.addrBuildingGroupData as BuildingGroupEntry[]).length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <BuildingGroupTable
                    entries={ticket.addrBuildingGroupData as BuildingGroupEntry[]}
                    onEntriesChange={() => {}}
                    readOnly
                  />
                </div>
              )}

              {ticket.jvid != null && ticket.addrLat != null && ticket.addrLon != null && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <AddressEnrichmentPanel ticket={ticket} />
                </div>
              )}
            </div>
            );
          })()}

          {/* Customer Contact panel — for all types when any field is non-null */}
          {(ticket.contactFirstName || ticket.contactLastName || ticket.contactPhone || ticket.contactEmail) && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-sm font-bold text-slate-800 mb-3">{t('tickets:detail.customerContact')}</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{t('common:fields.contactFirstName')}</p>
                  <p className="text-sm text-slate-700">{ticket.contactFirstName ?? '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{t('common:fields.contactLastName')}</p>
                  <p className="text-sm text-slate-700">{ticket.contactLastName ?? '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{t('common:fields.contactPhone')}</p>
                  <p className="text-sm text-slate-700">{ticket.contactPhone ?? '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{t('common:fields.contactEmail')}</p>
                  <p className="text-sm text-slate-700">{ticket.contactEmail ?? '—'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Support Request details (category + browser info) */}
          {ticket.ticketType === 'SUPPORT_REQUEST' && (ticket.supportCategory || ticket.browserInfo) && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-sm font-bold text-slate-800 mb-3">{t('tickets:detail.supportDetails')}</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2">
                {ticket.supportCategory && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{t('tickets:detail.fieldSupportCategory')}</p>
                    <span className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium bg-indigo-50 text-indigo-700 border-indigo-200">
                      <Tag className="w-3 h-3" />
                      {t(`common:supportCategory.${ticket.supportCategory.toLowerCase()}`, ticket.supportCategory)}
                    </span>
                  </div>
                )}
                {ticket.browserInfo && (
                  <>
                    {(ticket.browserInfo as Record<string, string>).platform && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{t('tickets:detail.fieldPlatform')}</p>
                        <p className="text-sm text-slate-700">{(ticket.browserInfo as Record<string, string>).platform}</p>
                      </div>
                    )}
                    {(ticket.browserInfo as Record<string, string>).screenSize && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{t('tickets:detail.fieldScreenSize')}</p>
                        <p className="text-sm font-mono text-slate-700">{(ticket.browserInfo as Record<string, string>).screenSize}</p>
                      </div>
                    )}
                    {(ticket.browserInfo as Record<string, string>).userAgent && (
                      <div className="col-span-2 lg:col-span-4">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{t('tickets:detail.fieldUserAgent')}</p>
                        <p className="text-xs font-mono text-slate-500 break-all">{(ticket.browserInfo as Record<string, string>).userAgent}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Build details — the DMP area snapshot taken at creation */}
          {ticket.ticketType === 'BUILD' && (ticket.areaId || ticket.buildSubcategory) && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-sm font-bold text-slate-800 mb-3">{t('tickets:detail.buildDetails')}</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3">
                {ticket.buildSubcategory && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{t('tickets:buildWizard.category')}</p>
                    <span className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700 border-emerald-200">
                      <Tag className="w-3 h-3" />
                      {t(`common:buildSubcategory.${ticket.buildSubcategory}`, ticket.buildSubcategory)}
                    </span>
                  </div>
                )}
                {ticket.areaId && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{t('common:fields.areaId')}</p>
                    <p className="text-sm font-mono text-slate-700">{ticket.areaId}</p>
                  </div>
                )}
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{t('common:fields.areaProject')}</p>
                  <p className="text-sm font-mono text-slate-700">{ticket.areaProject || '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{t('common:fields.areaReleased')}</p>
                  {/* Three-state: null means DMP records nothing, which is not "No". */}
                  <p className="text-sm text-slate-700">
                    {ticket.areaReleased === null || ticket.areaReleased === undefined
                      ? '—'
                      : ticket.areaReleased
                        ? t('common:fields.areaReleasedYes')
                        : t('common:fields.areaReleasedNo')}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{t('common:fields.areaReleasedDate')}</p>
                  {/* DMP's naive wall clock, sliced not parsed — no browser timezone applied. */}
                  <p className="text-sm text-slate-700">{ticket.areaReleasedDate?.slice(0, 10) || '—'}</p>
                </div>
                {ticket.areaName && (
                  <div className="col-span-2 lg:col-span-4">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{t('common:fields.areaName')}</p>
                    <p className="text-sm text-slate-700">{ticket.areaName}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Build — extra addresses this ticket refers to, each with full DMP data */}
          {ticket.ticketType === 'BUILD' && (
            <AttachedJvidsPanel
              ticket={ticket}
              onChange={(jvids) => setTicket((prev) => (prev ? { ...prev, attachedJvids: jvids } : prev))}
            />
          )}

          {/* Build — the area on a map, with the Cocon network around it */}
          {ticket.ticketType === 'BUILD' && ticket.areaId && (
            <BuildAreaMapPanel areaId={ticket.areaId} />
          )}

          {/* Similar Tickets */}
          <SimilarTicketsPanel ticket={ticket} />

          {/* Attachments */}
          <TicketAttachments
            ref={attachmentsComponentRef}
            ticketId={ticket.id}
            attachments={ticket.attachments}
            onTicketUpdate={(updated) => setTicket(updated)}
          />

          {/* Timeline */}
          <TicketTimeline updates={ticket.updates} ticketId={ticket.id} onRefresh={loadTicket} userMap={userMap} />

          {/* Comment input */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-3">
              <h3 className="text-sm font-bold text-slate-800">{t('tickets:detail.addComment')}</h3>
              {isInternal && (
                <label className="flex items-center gap-2 ml-auto cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={internalNote}
                      onChange={(e) => setInternalNote(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-slate-200 rounded-full peer-checked:bg-brand-purple transition-colors" />
                    <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow peer-checked:translate-x-4 transition-transform" />
                  </div>
                  <span className="text-xs font-medium text-slate-500">{t('tickets:detail.internalNote')}</span>
                  <Lock className="w-3.5 h-3.5 text-brand-purple/50" />
                </label>
              )}
            </div>
            <MentionInput
              value={commentText}
              onChange={setCommentText}
              mentionedUserIds={mentionedUserIds}
              onMentionedUserIdsChange={setMentionedUserIds}
              placeholder={t('tickets:detail.commentPlaceholder')}
              className="w-full px-4 py-3 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 transition-all placeholder:text-slate-400 resize-y"
            />
            <div className="flex items-center justify-between mt-3">
              <button onClick={() => attachmentsComponentRef.current?.triggerUpload()} className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-slate-500 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                <Paperclip className="w-4 h-4" />
                {t('tickets:detail.attachFile')}
              </button>
              <div className="flex items-center gap-2">
                {allowedTransitions.includes('RESOLVED') && (
                  <button
                    onClick={handlePostAndResolve}
                    disabled={posting || !commentText.trim()}
                    className="px-4 py-1.5 text-xs font-semibold rounded-lg border border-green-300 text-green-700 hover:bg-green-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                  >
                    {posting && <Loader2 className="w-3 h-3 animate-spin" />}
                    {t('tickets:detail.postAndResolve')}
                  </button>
                )}
                <button
                  onClick={handlePostComment}
                  disabled={posting || !commentText.trim()}
                  className="px-4 py-1.5 text-xs font-semibold text-white rounded-lg shadow-sm bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  {posting && <Loader2 className="w-3 h-3 animate-spin" />}
                  {posting ? t('tickets:detail.posting') : t('tickets:detail.postComment')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN (side panel) */}
        <div className="w-full lg:w-80 shrink-0 space-y-4">
          {/* SLA Status */}
          <SlaStatusPanel ticket={ticket} />

          {/* Quick Actions — PRD 4.1.4: conditional based on ticket status */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">{t('tickets:detail.actions')}</h3>
            <div className="space-y-2">
              {/* Change Status */}
              <div className="relative">
                <ActionButton
                  icon={<ArrowUpDown className="w-4 h-4 text-slate-400" />}
                  label={t('tickets:detail.changeStatus')}
                  onClick={() => setOpenAction(openAction === 'status' ? null : 'status')}
                />
                {openAction === 'status' && (
                  <div className="mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-2 space-y-1">
                    {allowedTransitions.length === 0 ? (
                      <p className="text-xs text-slate-400 px-2 py-1">{t('tickets:detail.noTransitions')}</p>
                    ) : (
                      allowedTransitions.map((s) => (
                        <button
                          key={s}
                          disabled={updating}
                          onClick={() => handleUpdate({ status: s })}
                          className="w-full text-left px-3 py-1.5 text-xs font-medium text-slate-700 rounded hover:bg-slate-50 transition-colors flex items-center gap-2 disabled:opacity-50"
                        >
                          {updating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 text-transparent" />}
                          {t(`common:status.${s}`)}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Reassign */}
              {canReassign && (
                <div className="relative">
                  <ActionButton
                    icon={<User className="w-4 h-4 text-slate-400" />}
                    label={t('tickets:detail.reassign')}
                    onClick={() => setOpenAction(openAction === 'reassign' ? null : 'reassign')}
                  />
                  {openAction === 'reassign' && (
                    <div className="mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-2 space-y-1 max-h-48 overflow-y-auto">
                      <button
                        disabled={updating}
                        onClick={() => handleUpdate({ assignedTo: null })}
                        className="w-full text-left px-3 py-1.5 text-xs font-medium text-slate-400 italic rounded hover:bg-slate-50 transition-colors disabled:opacity-50"
                      >
                        {t('tickets:detail.unassign')}
                      </button>
                      {internalUsers.map((u) => (
                        <button
                          key={u.id}
                          disabled={updating}
                          onClick={() => handleUpdate({ assignedTo: u.id })}
                          className="w-full text-left px-3 py-1.5 text-xs font-medium text-slate-700 rounded hover:bg-slate-50 transition-colors flex items-center gap-2 disabled:opacity-50"
                        >
                          {ticket.assignee?.id === u.id ? <Check className="w-3 h-3 text-brand-rose" /> : <Check className="w-3 h-3 text-transparent" />}
                          {u.displayName}
                        </button>
                      ))}
                      {internalUsers.length === 0 && (
                        <p className="text-xs text-slate-400 px-2 py-1 flex items-center gap-1">
                          <Loader2 className="w-3 h-3 animate-spin" /> {t('tickets:detail.loadingUsers')}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Change Priority */}
              {canChangePriority && (
                <div className="relative">
                  <ActionButton
                    icon={<Flag className="w-4 h-4 text-slate-400" />}
                    label={t('tickets:detail.changePriority')}
                    onClick={() => setOpenAction(openAction === 'priority' ? null : 'priority')}
                  />
                  {openAction === 'priority' && (() => {
                    const currentLevel = PRIORITY_LEVEL[ticket.priority] ?? 0;
                    // External users can only lower priority; internal users see all options
                    const options = Object.values(PRIORITIES).filter((p) =>
                      isInternal || (PRIORITY_LEVEL[p] ?? 0) <= currentLevel
                    );
                    return (
                      <div className="mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-2 space-y-1">
                        {options.filter((p) => p !== ticket.priority).length === 0 ? (
                          <p className="text-xs text-slate-400 px-2 py-1">{t('tickets:detail.noPriorityChanges')}</p>
                        ) : (
                          options.map((p) => (
                            <button
                              key={p}
                              disabled={updating || p === ticket.priority}
                              onClick={() => handleUpdate({ priority: p })}
                              className="w-full text-left px-3 py-1.5 text-xs font-medium text-slate-700 rounded hover:bg-slate-50 transition-colors flex items-center gap-2 disabled:opacity-50"
                            >
                              {ticket.priority === p ? <Check className="w-3 h-3 text-brand-rose" /> : <Check className="w-3 h-3 text-transparent" />}
                              {t(`common:priority.${p}`)}
                            </button>
                          ))
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* Watchers */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">{t('tickets:detail.watchers')}</h3>
              <button onClick={() => {
                const willShow = !showWatcherAdd;
                setShowWatcherAdd(willShow);
                if (!willShow) setWatcherSearch('');
                if (willShow && allUsers.length === 0) {
                  loadAllUsers()
                    .catch(() => toast.error(t('tickets:detail.loadUsersError')));
                }
              }} className="text-xs font-medium text-brand-rose hover:text-brand-purple transition-colors">
                {showWatcherAdd ? <X className="w-3 h-3 inline mr-0.5" /> : <Plus className="w-3 h-3 inline mr-0.5" />}
                {showWatcherAdd ? t('common:actions.close') : t('common:actions.add')}
              </button>
            </div>
            {showWatcherAdd && (
              <div className="mb-3 bg-slate-50 border border-slate-200 rounded-lg p-2">
                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <input
                    type="text"
                    value={watcherSearch}
                    onChange={(e) => setWatcherSearch(e.target.value)}
                    placeholder={t('tickets:detail.searchUsers')}
                    className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 transition-all placeholder:text-slate-400"
                  />
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {allUsers.length === 0 ? (
                    <p className="text-xs text-slate-400 px-2 py-1 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> {t('tickets:detail.loadingUsers')}
                    </p>
                  ) : (
                    <>
                      {filteredWatcherUsers.internal.length > 0 && (
                        <>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-2 pt-1">
                            {t('tickets:detail.internalUsers')}
                          </p>
                          {filteredWatcherUsers.internal.map((u) => (
                            <button
                              key={u.id}
                              onClick={() => handleAddWatcher(u.id)}
                              className="w-full text-left px-3 py-1.5 text-xs font-medium text-slate-700 rounded hover:bg-white transition-colors flex items-center gap-2"
                            >
                              <div className="w-5 h-5 rounded-full bg-brand-rose/10 flex items-center justify-center text-[8px] font-bold text-brand-rose">
                                {getInitials(u.displayName)}
                              </div>
                              {u.displayName}
                            </button>
                          ))}
                        </>
                      )}
                      {filteredWatcherUsers.external.length > 0 && (
                        <>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-2 pt-1">
                            {t('tickets:detail.externalUsers')}
                          </p>
                          {filteredWatcherUsers.external.map((u) => (
                            <button
                              key={u.id}
                              onClick={() => handleAddWatcher(u.id)}
                              className="w-full text-left px-3 py-1.5 text-xs font-medium text-slate-700 rounded hover:bg-white transition-colors flex items-center gap-2"
                            >
                              <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-[8px] font-bold text-slate-600">
                                {getInitials(u.displayName)}
                              </div>
                              {u.displayName}
                              <span className="text-[10px] text-slate-400 ml-auto">{t('tickets:detail.external')}</span>
                            </button>
                          ))}
                        </>
                      )}
                      {filteredWatcherUsers.internal.length === 0 && filteredWatcherUsers.external.length === 0 && (
                        <p className="text-xs text-slate-400 px-2 py-1">
                          {watcherSearch.trim() ? t('tickets:detail.noUsersFound') : t('tickets:detail.allWatching')}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
            <div className="space-y-2">
              {[...ticket.watchers].sort((a, b) => a.displayName.localeCompare(b.displayName)).map((w) => {
                const isSelf = w.userId === authUser?.id;
                const canRemove = isSelf || isAdmin;
                return (
                  <div key={w.userId} className="flex items-center gap-2 group">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold ${isSelf ? 'bg-brand-rose/10 text-brand-rose' : 'bg-slate-200 text-slate-600'}`}>
                      {getInitials(w.displayName)}
                    </div>
                    <span className={`text-sm flex-1 ${isSelf ? 'font-medium text-slate-800' : 'text-slate-700'}`}>
                      {w.displayName}{isSelf ? ` ${t('tickets:detail.you')}` : ''}
                    </span>
                    {canRemove && (
                      <button
                        onClick={() => handleRemoveWatcher(w.userId)}
                        className={`p-0.5 transition-all ${isSelf ? 'text-slate-400 hover:text-red-500' : 'opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500'}`}
                        title={t('tickets:detail.removeWatcher')}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
              {ticket.watchers.length === 0 && (
                <p className="text-xs text-slate-400">{t('tickets:detail.noWatchers')}</p>
              )}
            </div>
          </div>


        </div>
      </div>

      <EditTicketDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        ticket={ticket}
        onSave={async (data) => { await handleUpdate(data); setEditOpen(false); }}
        updating={updating}
      />
      <EscalateTicketDialog
        open={escalateOpen}
        onOpenChange={setEscalateOpen}
        ticketNumber={ticket.ticketNumber}
        mode={escalateMode}
        onConfirm={async (data) => { await handleUpdate(data); setEscalateOpen(false); }}
        updating={updating}
      />
    </div>
  );
}

function ActionButton({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick?: () => void }) {
  return (
    <button onClick={onClick} className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors text-left">
      {icon}
      {label}
    </button>
  );
}

function DMPField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{label}</p>
      <p className={`text-sm ${mono ? 'font-mono font-medium text-slate-800' : 'text-slate-700'}`}>{value}</p>
    </div>
  );
}

// ── SLA helpers ────────────────────────────────────

function formatTimeStr(ms: number) {
  const abs = Math.abs(ms);
  const hours = Math.floor(abs / 3600_000);
  const days = Math.floor(hours / 24);
  const rem = hours % 24;
  return days > 0 ? (rem > 0 ? `${days}d ${rem}h` : `${days}d`) : `${hours}h`;
}

function computeSlaState(deadline: string, createdAt: string, isResolved: boolean, metFlag: boolean | null) {
  const now = Date.now();
  const deadlineMs = new Date(deadline).getTime();
  const createdMs = new Date(createdAt).getTime();
  const totalDuration = deadlineMs - createdMs;
  const elapsed = now - createdMs;
  const remaining = deadlineMs - now;

  let status: 'on_track' | 'at_risk' | 'breached' | 'met';
  if (isResolved) {
    status = metFlag ? 'met' : 'breached';
  } else if (remaining <= 0) {
    status = 'breached';
  } else if (totalDuration > 0 && elapsed / totalDuration >= 0.8) {
    status = 'at_risk';
  } else {
    status = 'on_track';
  }

  const progress = totalDuration > 0 ? Math.min(Math.max((elapsed / totalDuration) * 100, 0), 100) : 0;
  return { status, progress, remaining, deadlineMs };
}

const slaColorMap = {
  on_track: { stroke: '#22c55e', text: 'text-green-600', bg: '#dcfce7' },
  at_risk: { stroke: '#f59e0b', text: 'text-yellow-600', bg: '#fef3c7' },
  breached: { stroke: '#ef4444', text: 'text-red-600', bg: '#fee2e2' },
  met: { stroke: '#22c55e', text: 'text-green-600', bg: '#dcfce7' },
};

// ── SLA Status Panel ────────────────────────────────────

function SlaStatusPanel({ ticket }: { ticket: TicketDetailType }) {
  const { t } = useTranslation(['tickets', 'common']);
  const isResolved = ticket.status === 'RESOLVED' || ticket.status === 'CLOSED';

  const hasResponse = !!ticket.slaResponseDeadline;
  const hasResolution = !!ticket.slaResolutionDeadline;

  // No SLA configured at all
  if (!hasResponse && !hasResolution) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">{t('tickets:detail.slaStatus')}</h3>
        <p className="text-sm text-slate-400">{t('common:sla.noSlaConfigured')}</p>
      </div>
    );
  }

  // Resolution SLA (primary — shown with circle)
  const resolution = hasResolution
    ? computeSlaState(ticket.slaResolutionDeadline!, ticket.createdAt, isResolved, ticket.slaResolutionMet ?? null)
    : null;

  // Response SLA (secondary — shown as compact row)
  const response = hasResponse
    ? computeSlaState(ticket.slaResponseDeadline!, ticket.createdAt, isResolved, ticket.slaResponseMet ?? null)
    : null;

  // Use resolution for the primary display; fallback to response if only response exists
  const primary = resolution ?? response!;
  const primaryLabel = resolution ? t('tickets:detail.slaResolution') : t('tickets:detail.slaResponse');
  const colors = slaColorMap[primary.status];
  const displayPct = Math.round(100 - primary.progress);
  const circumference = 2 * Math.PI * 28;
  const strokeOffset = circumference * (primary.progress / 100);

  const statusLabelMap = {
    on_track: t('common:sla.onTrack'),
    at_risk: t('common:sla.atRisk'),
    breached: t('common:sla.breached'),
    met: t('common:sla.met'),
  };

  function makeTimeLabel(state: ReturnType<typeof computeSlaState>, c: typeof colors) {
    const timeStr = formatTimeStr(state.remaining);
    if (state.status === 'met') return <span className={`font-semibold ${c.text}`}>{t('common:sla.resolvedWithinSla')}</span>;
    if (state.remaining <= 0) return <>{t('common:sla.overdueBy')} <span className={`font-semibold ${c.text}`}>{timeStr}</span></>;
    return <>{t('common:sla.timeRemaining')} <span className={`font-semibold ${c.text}`}>{timeStr}</span></>;
  }

  const deadlineFmt = (dl: string) => {
    const d = new Date(dl);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ', ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">{t('tickets:detail.slaStatus')}</h3>

      {/* Primary SLA (circle + details) */}
      <div className="flex items-center gap-4">
        <div className="relative w-16 h-16 shrink-0">
          <svg className="w-16 h-16" viewBox="0 0 64 64">
            <circle cx="32" cy="32" r="28" fill="none" stroke={colors.bg} strokeWidth="5" />
            <circle
              cx="32" cy="32" r="28" fill="none"
              stroke={colors.stroke}
              strokeWidth="5"
              strokeDasharray={String(circumference)}
              strokeDashoffset={String(strokeOffset)}
              strokeLinecap="round"
              className="-rotate-90 origin-center"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            {primary.status === 'breached' ? (
              <AlertTriangle className="w-5 h-5 text-red-500" />
            ) : (
              <span className={`text-xs font-bold ${colors.text}`}>{displayPct}%</span>
            )}
          </div>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{primaryLabel}</p>
          <p className={`text-lg font-bold ${colors.text}`}>{statusLabelMap[primary.status]}</p>
          <p className="text-xs text-slate-500">{makeTimeLabel(primary, colors)}</p>
          <p className="text-xs text-slate-400 mt-0.5">{t('common:sla.deadline')} {deadlineFmt(resolution ? ticket.slaResolutionDeadline! : ticket.slaResponseDeadline!)}</p>
        </div>
      </div>

      {/* Secondary SLA — response (compact row, only if both exist) */}
      {resolution && response && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{t('tickets:detail.slaResponse')}</p>
            {(() => {
              const rc = slaColorMap[response.status];
              return (
                <span className={`text-xs font-semibold ${rc.text}`}>{statusLabelMap[response.status]}</span>
              );
            })()}
          </div>
          <div className="flex items-center justify-between mt-1">
            <p className="text-xs text-slate-500">{makeTimeLabel(response, slaColorMap[response.status])}</p>
            <p className="text-xs text-slate-400">{deadlineFmt(ticket.slaResponseDeadline!)}</p>
          </div>
        </div>
      )}
    </div>
  );
}
